/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect } from 'react';

import { COLORS } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { PhotoDateEditor, type PhotoDateEditorStyles } from '@/Client/Partials/PhotoDetail';
import { formatMonthDayTime, photoTagsPresenter } from '@/Client/Presenters';
import { usePhotoFullSizeUri, useUser } from '@kaiizen/kai-pro-api-react-state';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import { getCellPhotos, type ProgressAltCursorDirection } from './progressAltCursorLib';
import { ProgressAltPhotoDetails } from './ProgressAltPhotoDetails';
import { ProgressAltPhotoFilmstrip } from './ProgressAltPhotoFilmstrip';

const VIEW_STYLE = css`
  label: PROGRESS_ALT_PHOTO_VIEW_STYLE;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: ${COLORS.appBg};
`;

const STAGE_STYLE = css`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * The photo gets the whole stage and no zoom control. At this size it is already larger than the tile
 * that was clicked by more than a magnifier would add, so the room goes to the picture rather than to
 * chrome for inspecting it.
 */
const PHOTO_STYLE = css`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
`;

/**
 * Fills the stage and lets `contain` do the scaling, rather than capping at the file's own pixel size.
 * A photo smaller than the stage is scaled up to it — the stage is the size the reader asked for when
 * they opened the view, and a small file rendered small in the middle of a black field reads as broken.
 * `contain` keeps the whole frame, so scaling up never crops.
 */
const IMAGE_STYLE = css`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

/**
 * Everything there is to say about the photo itself, on the photo. The right-hand pane describes the
 * scope; repeating the capture details there would only print them twice.
 *
 * Wears the closeout modal's caption bar as-is — body type, sentence case. This is a line of prose
 * about the picture, not a label on a control, and the mono capitals the panel's chrome uses would
 * make dates and names harder to read rather than easier.
 */
const CAPTION_STYLE = css`
  flex: 0 0 auto;
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: ${pxToRem(12)};
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* The pencil in the caption bar, in the bar's own white: dimmed until hovered, sized to its line. */
const CAPTION_DATE_EDITOR_STYLES: PhotoDateEditorStyles = {
  editButton: css`
    label: PROGRESS_ALT_CAPTION_DATE_EDIT;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    margin-left: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;

    &:hover {
      color: #fff;
    }
  `,
};

const EMPTY_STYLE = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.midtone};
  font-size: ${pxToRem(14)};
`;

/** Which move each arrow key makes: across for scope, up and down for time. */
const DIRECTION_BY_ARROW_KEY: Record<string, ProgressAltCursorDirection> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'earlier',
  ArrowDown: 'later',
};

/**
 * Progress Alt with one cell blown up — a mode of the panel, not a dialog over it.
 *
 * It navigates in the grid's own terms so the reader never loses their place: the time moves sit at the
 * left where the date column was, the scope moves sit at the right beside the scope they name, and the
 * strip along the bottom is the rest of that one cell. Every move lands on a cell that actually holds
 * photos, and closing returns to the grid looking at wherever this ended up.
 *
 * Reads the whole of what it draws off `progressAltContext`, so an edit anywhere in the scan reaches
 * this without passing through the grid or the panel.
 */
export const ProgressAltPhotoView = () => {
  const {
    timeline,
    cursor,
    photoScope,
    photoNeighbors,
    movePhotoCursor,
    closePhotoView,
    canEditPhotoDate,
    setPhotoDate,
  } = useProgressAlt();

  const photos = timeline && cursor ? getCellPhotos(timeline, cursor.rowIndex, cursor.columnIndex) : [];
  const photoIndex = photos.findIndex(candidate => candidate.photoId === cursor?.photoId);
  const photo = photos[photoIndex];
  const column = photoScope?.column;

  const imageUri = usePhotoFullSizeUri(cursor?.photoId);
  const { user } = useUser(photo?.addedByUserId);

  // The four moves and the way out, on the keys the grid's shape implies. Bound on the window because
  // the stage holds no focusable element of its own.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const direction = DIRECTION_BY_ARROW_KEY[event.key];
      if (!direction && event.key !== 'Escape') return;
      event.preventDefault();
      if (direction) movePhotoCursor(direction);
      else closePhotoView();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [movePhotoCursor, closePhotoView]);

  if (!timeline || !cursor) return null;
  if (!column) return <div css={EMPTY_STYLE}>This photo&rsquo;s scope is no longer in the timeline.</div>;

  // Same wording as the strip's own captions, so a thumbnail and the photo it opens read alike.
  const dateLabel = photo?.addedAt ? `added ${formatMonthDayTime(photo.addedAt)}` : undefined;
  const caption = [
    `${Math.max(photoIndex, 0) + 1} of ${photos.length}`,
    // With testing tools on the date carries a pencil, and an undated photo says so, so it can be dated too.
    photo && canEditPhotoDate
      ? (
        <span key='date'>
          {dateLabel ?? 'undated'}
          <PhotoDateEditor
            addedAt={photo.addedAt}
            onCommit={addedAt => setPhotoDate(photo.photoId, addedAt)}
            placement='above'
            styles={CAPTION_DATE_EDITOR_STYLES}
          />
        </span>
      )
      : dateLabel,
    user ? `by ${user.name}` : undefined,
    photo?.photoSource ? `from ${photo.photoSource}` : undefined,
    photo?.tags?.length ? photo.tags.map(photoTagsPresenter).join(', ') : undefined,
  ].filter(Boolean).flatMap((segment, index) => index ? [' · ', segment] : [segment]);

  return (
    <div css={VIEW_STYLE} data-testid='progress-alt-photo-view'>
      <div css={STAGE_STYLE}>
        <div css={PHOTO_STYLE}>
          {imageUri ? <img css={IMAGE_STYLE} src={imageUri} alt={`${column.title} photo`} /> : null}
        </div>
        <div css={CAPTION_STYLE} data-testid='progress-alt-photo-caption'>{caption}</div>

        <ProgressAltPhotoFilmstrip photos={photos} />
      </div>

      <ProgressAltPhotoDetails />
    </div>
  );
};
